import { useState, useMemo } from "react";
import "./analytics-workspace.css";
import { useQuery } from "@tanstack/react-query";
import { DashboardDateProvider, useDashboardDate } from "@/contexts/DashboardDateContext";
import { DashboardAttributionProvider, useDashboardAttribution, type AttributionWindow } from "@/contexts/DashboardAttributionContext";
import { DashboardDataSourceProvider, isDemoAccount, type DataSourceType } from "@/contexts/DashboardDataSourceContext";
import { DashboardLayoutEngine } from "@/components/dashboard/DashboardLayoutEngine";
import { useDashboardAccounts } from "@/contexts/DashboardAccountContext";
import { useMetaAccount } from "@/contexts/MetaAccountContext";
import { PlatformFilterToggle } from "@/components/dashboard/PlatformFilterToggle";
import { UniversalAccountSelector } from "@/components/dashboard/UniversalAccountSelector";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar, Settings2, ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { CanvasCrmTab } from "./canvas-crm-tab";
import { SettingsModal, type SettingsTab } from "@/components/settings-modal";
import { AnalyticsCoverageNotice } from '@/components/dashboard/AnalyticsCoverageNotice';
import { AnalyticsExportButton } from '@/components/dashboard/AnalyticsExportButton';

function CanvasAnalyticsHeader({ accountName, isEditMode, setIsEditMode }: {
  accountName: string;
  isEditMode: boolean;
  setIsEditMode: (v: boolean) => void;
}) {
  const { startDate, endDate, presetKey, setPreset, setCustomRange, presets, timezoneAbbr } = useDashboardDate();
  const { attributionWindow, setAttributionWindow, windowLabel } = useDashboardAttribution();
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(startDate);
  const [customEnd, setCustomEnd] = useState(endDate);

  const currentPreset = presets.find(p => p.key === presetKey);
  const dateLabel = presetKey === "custom"
    ? `${startDate} — ${endDate}`
    : (currentPreset ? currentPreset.label : `${startDate} — ${endDate}`);

  const attributionOptions: Array<{ value: AttributionWindow; label: string }> = [
    { value: "account_default", label: "Account settings" },
    { value: "1d_click", label: "1-day click" },
    { value: "7d_click", label: "7-day click" },
    { value: "28d_click", label: "28-day click" },
    { value: "1d_view", label: "1-day view" },
    { value: "7d_view", label: "7-day view" },
    { value: "7d_click_1d_view", label: "7d click, 1d view" },
  ];

  const handleApplyCustom = () => {
    if (customStart && customEnd && customStart <= customEnd) {
      setCustomRange(customStart, customEnd);
      setShowCustom(false);
    }
  };

  const pillBase = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer select-none";
  const pillStyle = {
    background: "#F7F9FC",
    border: "1px solid #e3e8ef",
    color: "#657084",
  };
  const pillHoverStyle = {
    background: "#FFFFFF",
  };

  return (
    <div
      className="analytics-filter-bar flex-shrink-0 px-6 py-4 border-b"
      style={{ borderColor: "#e3e8ef", background: "#FFFFFF" }}
      data-testid="canvas-analytics-header"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2
            className="text-xl font-semibold"
            style={{ color: "#1A1A1A", letterSpacing: "-0.02em" }}
            data-testid="canvas-analytics-title"
          >
            Performance overview
          </h2>
          <p
            className="text-xs mt-0.5"
            style={{ color: "#5F6D82" }}
            data-testid="canvas-analytics-subtitle"
          >
            {accountName}
          </p>
        </div>

        <div className="analytics-filter-controls flex flex-wrap items-center gap-2">
          <div style={{ display: "contents" }}>
            <PlatformFilterToggle />
          </div>
          <div style={{ display: "contents" }}>
            <UniversalAccountSelector />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button
                className={pillBase}
                style={pillStyle}
                data-testid="canvas-analytics-date-picker"
                onMouseEnter={e => Object.assign((e.currentTarget as HTMLElement).style, pillHoverStyle)}
                onMouseLeave={e => Object.assign((e.currentTarget as HTMLElement).style, { background: "#F7F9FC" })}
              >
                <Calendar style={{ width: 13, height: 13, color: "#5F6D82" }} />
                <span>{dateLabel}</span>
                {timezoneAbbr && (
                  <span style={{ fontSize: 10, color: "#94A3B8" }}>{timezoneAbbr}</span>
                )}
                <ChevronDown style={{ width: 11, height: 11, color: "#94A3B8" }} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-1" align="end" style={{ border: "1px solid #e3e8ef", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
              {presets.map(p => (
                <button
                  key={p.key}
                  onClick={() => { setPreset(p.key); setShowCustom(false); }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-xs rounded-md transition-colors flex items-center justify-between",
                    presetKey === p.key
                      ? "text-[#7b1f40] font-medium"
                      : "text-[#334155] hover:bg-[#F7F9FC]"
                  )}
                  style={presetKey === p.key ? { background: "#f5e8ee" } : {}}
                  data-testid={`canvas-analytics-date-preset-${p.key}`}
                >
                  {p.label}
                  {presetKey === p.key && <Check style={{ width: 12, height: 12, color: "#7b1f40" }} />}
                </button>
              ))}
              <div style={{ borderTop: "1px solid #e3e8ef", marginTop: 4, paddingTop: 4 }}>
                <button
                  onClick={() => setShowCustom(!showCustom)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-xs rounded-md transition-colors flex items-center justify-between",
                    presetKey === "custom"
                      ? "text-[#7b1f40] font-medium"
                      : "text-[#334155] hover:bg-[#F7F9FC]"
                  )}
                  style={presetKey === "custom" ? { background: "#f5e8ee" } : {}}
                  data-testid="canvas-analytics-date-preset-custom"
                >
                  Custom
                  {presetKey === "custom" && <Check style={{ width: 12, height: 12, color: "#7b1f40" }} />}
                </button>
                {showCustom && (
                  <div className="px-3 py-2 space-y-2">
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 500, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Start</label>
                      <Input
                        type="date"
                        value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="h-7 text-xs mt-1"
                        style={{ border: "1px solid #e3e8ef", background: "#F7F9FC" }}
                        data-testid="canvas-analytics-date-start"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 500, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>End</label>
                      <Input
                        type="date"
                        value={customEnd}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="h-7 text-xs mt-1"
                        style={{ border: "1px solid #e3e8ef", background: "#F7F9FC" }}
                        data-testid="canvas-analytics-date-end"
                      />
                    </div>
                    <button
                      onClick={handleApplyCustom}
                      disabled={!customStart || !customEnd || customStart > customEnd}
                      className="w-full text-xs py-1.5 rounded-md font-medium transition-colors disabled:opacity-40"
                      style={{ background: "#7b1f40", color: "#FFFFFF" }}
                      data-testid="canvas-analytics-date-apply"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={pillBase}
                style={pillStyle}
                data-testid="canvas-analytics-attribution-picker"
                onMouseEnter={e => Object.assign((e.currentTarget as HTMLElement).style, pillHoverStyle)}
                onMouseLeave={e => Object.assign((e.currentTarget as HTMLElement).style, { background: "#F7F9FC" })}
              >
                <span>{windowLabel}</span>
                <ChevronDown style={{ width: 11, height: 11, color: "#94A3B8" }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ border: "1px solid #e3e8ef", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
              {attributionOptions.map(opt => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => setAttributionWindow(opt.value)}
                  className={cn(
                    "text-xs",
                    attributionWindow === opt.value ? "text-[#7b1f40] font-medium" : "text-[#334155]"
                  )}
                  style={attributionWindow === opt.value ? { background: "#f5e8ee" } : {}}
                >
                  {opt.label}
                  {attributionWindow === opt.value && <Check style={{ width: 12, height: 12, marginLeft: "auto", color: "#7b1f40" }} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!isEditMode && (
            <button
              className={pillBase}
              style={pillStyle}
              onClick={() => setIsEditMode(true)}
              data-testid="canvas-analytics-customize"
              onMouseEnter={e => Object.assign((e.currentTarget as HTMLElement).style, pillHoverStyle)}
              onMouseLeave={e => Object.assign((e.currentTarget as HTMLElement).style, { background: "#F7F9FC" })}
            >
              <Settings2 style={{ width: 13, height: 13, color: "#5F6D82" }} />
              <span>Customize</span>
            </button>
          )}

          <AnalyticsExportButton />
        </div>
      </div>
    </div>
  );
}

function CanvasAnalyticsContent() {
  const [isEditMode, setIsEditMode] = useState(false);
  // v4.74 -- "Connect Meta Account" on the empty state opens the Settings ->
  // Connections modal (same flow as the chat shortcut), not a raw OAuth redirect.
  const [connectOpen, setConnectOpen] = useState(false);
  const [activeView, setActiveView] = useState<"ads" | "crm">("ads");
  const [showCrmSettings, setShowCrmSettings] = useState(false);

  const {
    selectedAccountIds,
    allAccounts: unifiedAccounts,
    selectedAccounts,
    activePlatforms,
    isAllSelected,
    brandId,
  } = useDashboardAccounts();

  const { selectedAccountId, selectedAccount, allAccounts: metaAccounts } = useMetaAccount();

  const { data: mockStatus } = useQuery({
    queryKey: ["/api/mock-insights/status"],
    queryFn: async () => {
      const res = await fetch("/api/mock-insights/status");
      if (!res.ok) throw new Error("Failed to fetch status");
      return res.json();
    },
  });

  const mockBrands: Array<{ brandId: number; accountName: string }> = mockStatus?.brands || [];
  const demoAccounts = metaAccounts.filter(a => isDemoAccount(a));

  const hasUnifiedAccounts = unifiedAccounts.length > 0;
  const hasLegacyAccounts = metaAccounts.length > 0;
  const useUnifiedMode = hasUnifiedAccounts;

  const resolveMockBrandForAccount = (account: typeof metaAccounts[0]): number | null => {
    const idx = demoAccounts.findIndex(a => a.id === account.id);
    if (idx >= 0 && idx < mockBrands.length) return mockBrands[idx].brandId;
    if (mockBrands.length > 0) return mockBrands[0].brandId;
    return null;
  };

  const resolveForAccount = (account: typeof metaAccounts[0]): {
    sourceType: DataSourceType;
    sourceId: number;
    layoutKey: number;
  } => {
    if (isDemoAccount(account)) {
      const bId = resolveMockBrandForAccount(account);
      return { sourceType: "mock", sourceId: bId ?? 0, layoutKey: bId ?? 0 };
    }
    return { sourceType: "meta", sourceId: account.id, layoutKey: account.id };
  };

  const resolveLegacyDataSource = (): {
    sourceType: DataSourceType;
    sourceId: number;
    layoutKey: number;
  } => {
    const isAllAccountsMode = selectedAccountId === null;
    if (isAllAccountsMode) {
      if (demoAccounts.length > 0 && mockBrands.length > 0) {
        return { sourceType: "all", sourceId: -1, layoutKey: 0 };
      }
      const realAccounts = metaAccounts.filter(a => !isDemoAccount(a));
      if (realAccounts.length > 0) return resolveForAccount(realAccounts[0]);
      if (metaAccounts.length > 0) return resolveForAccount(metaAccounts[0]);
      return { sourceType: "mock", sourceId: 0, layoutKey: 0 };
    }
    if (selectedAccount) return resolveForAccount(selectedAccount);
    if (metaAccounts.length > 0) return resolveForAccount(metaAccounts[0]);
    if (mockBrands.length > 0) return { sourceType: "mock", sourceId: mockBrands[0].brandId, layoutKey: mockBrands[0].brandId };
    return { sourceType: "mock", sourceId: 0, layoutKey: 0 };
  };

  const mappedBrandIds = useMemo(() => {
    return demoAccounts
      .map((_, idx) => idx < mockBrands.length ? mockBrands[idx].brandId : null)
      .filter((id): id is number => id !== null);
  }, [demoAccounts, mockBrands]);

  const tabSwitcher = (
    <div
      className="analytics-page-heading flex-shrink-0 px-5 py-4 border-b flex flex-wrap items-center justify-between gap-4"
      style={{ borderColor: "#e3e8ef", background: "#FFFFFF" }}
      data-testid="analytics-view-tab-switcher"
    >
      <div>
        <p className="analytics-eyebrow">YOUR PERFORMANCE, IN FOCUS</p>
        <h1 className="workspace-page-title">Analytics</h1>
        <p className="workspace-page-description">Review your ad performance and customer activity.</p>
      </div>
      <div className="analytics-view-tabs" role="group" aria-label="Analytics views">
        {(["ads", "crm"] as const).map(view => (
          <button
            key={view}
            aria-pressed={activeView === view}
            data-testid={`analytics-tab-${view}`}
            onClick={() => setActiveView(view)}
            style={{
              padding: "4px 12px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: activeView === view ? 600 : 400,
              color: activeView === view ? "#1A1A1A" : "#657084",
              background: activeView === view ? "#FFFFFF" : "transparent",
              boxShadow: activeView === view ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              border: "none",
              cursor: "pointer",
              transition: "all 120ms",
            }}
          >
            {view === "ads" ? "Ads Analytics" : "CRM"}
          </button>
        ))}
      </div>
    </div>
  );

  if (activeView === "crm") {
    return (
      <div className="flex flex-col min-h-full" data-testid="analytics-crm-view">
        {tabSwitcher}
        {showCrmSettings && (
          <SettingsModal
            onClose={() => setShowCrmSettings(false)}
            initialTab="crm"
          />
        )}
        <CanvasCrmTab onOpenSettings={() => setShowCrmSettings(true)} />
      </div>
    );
  }

  if (!hasUnifiedAccounts && !hasLegacyAccounts && mockBrands.length === 0) {
    return (
      <div className="flex flex-col min-h-full" data-testid="canvas-analytics-empty-wrapper">
        {tabSwitcher}
        <div className="flex-1 flex items-center justify-center p-5 sm:p-12" data-testid="canvas-analytics-empty">
          <div
            className="rounded-xl p-6 sm:p-10 text-center max-w-md w-full"
            style={{ background: "#FFFFFF", border: "1px solid #e3e8ef" }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "#f5e8ee" }}
            >
              <Settings2 style={{ width: 20, height: 20, color: "#94A3B8" }} />
            </div>
            <h2 className="text-base font-semibold mb-2" style={{ color: "#1A1A1A" }}>
              See how your ads perform
            </h2>
            <p className="text-sm" style={{ color: "#5F6D82" }}>
              Connect your Meta ad account to see performance in one place. You can create ads before connecting an account.
            </p>
            <button
              data-testid="button-connect-meta-analytics"
              onClick={() => setConnectOpen(true)}
              className="mt-5 inline-flex items-center gap-2 mx-auto"
              style={{ padding: "9px 20px", borderRadius: 10, border: "none", background: "#1A1A1A", color: "#FFFFFF", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}
            >
              Connect Meta Account
            </button>
          </div>
        </div>
        {connectOpen && (
          <SettingsModal initialTab="connections" onClose={() => setConnectOpen(false)} />
        )}
      </div>
    );
  }

  if (useUnifiedMode) {
    const accountName = isAllSelected
      ? `All platforms · ${unifiedAccounts.length} ${unifiedAccounts.length === 1 ? "account" : "accounts"}`
      : selectedAccounts.length === 1
        ? selectedAccounts[0].accountName
        : `${selectedAccounts.length} accounts selected`;

    const layoutKey = selectedAccountIds.length > 0 ? selectedAccountIds[0] : 0;

    return (
      <DashboardDataSourceProvider
        sourceType="unified"
        sourceId={-1}
        accountIds={selectedAccountIds}
        platformSlugs={activePlatforms}
        brandId={brandId}
      >
        {tabSwitcher}
        <CanvasAnalyticsHeader
          accountName={accountName}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
        />
        {selectedAccounts.some(account => account.isDemo) && (
          <div role="status" data-testid="analytics-simulated-data-notice" className="analytics-source-notice mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <strong>Simulated test data.</strong> The selected test accounts contain generated performance metrics for dashboard testing. They are not Meta delivery, spend or conversion results.
          </div>
        )}
        {selectedAccounts.some(account => account.externalAccountId === "act_1209763144947816") && (
          <div role="status" data-testid="analytics-meta-sandbox-notice" className="analytics-source-notice mx-6 mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
            <strong>Meta sandbox account.</strong> Campaigns and ads come from Meta’s test account. Sandbox ads do not deliver, and Meta does not provide performance Insights for this account.
          </div>
        )}
        <AnalyticsCoverageNotice />
        <div className="analytics-grid-section p-6" style={{ background: "transparent" }}>
          <DashboardLayoutEngine
            brandId={layoutKey}
            isEditMode={isEditMode}
            onEditModeChange={setIsEditMode}
          />
        </div>
      </DashboardDataSourceProvider>
    );
  }

  const { sourceType, sourceId, layoutKey } = resolveLegacyDataSource();

  if (sourceId === 0) {
    const accountName = "Ad Account";
    return (
      <>
        {tabSwitcher}
        <CanvasAnalyticsHeader
          accountName={accountName}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
        />
        <div className="flex-1 flex items-center justify-center p-12">
          <p style={{ color: "#5F6D82", fontSize: 13 }}>Loading analytics data…</p>
        </div>
      </>
    );
  }

  const accountName = (() => {
    const isAllAccountsMode = selectedAccountId === null;
    if (isAllAccountsMode) {
      const count = metaAccounts.length;
      return count > 1 ? `All Accounts (${count})` : count === 1 ? metaAccounts[0].accountName : "All Accounts";
    }
    if (selectedAccount) return selectedAccount.nickname || selectedAccount.accountName;
    return "Ad Account";
  })();

  return (
    <DashboardDataSourceProvider sourceType={sourceType} sourceId={sourceId} allBrandIds={mappedBrandIds}>
      {tabSwitcher}
      <CanvasAnalyticsHeader
        accountName={accountName}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
      />
      <div className="analytics-grid-section p-6" style={{ background: "transparent" }}>
        <DashboardLayoutEngine
          brandId={layoutKey}
          isEditMode={isEditMode}
          onEditModeChange={setIsEditMode}
        />
      </div>
    </DashboardDataSourceProvider>
  );
}

export function CanvasAnalyticsView() {
  return (
    <div className="analytics-workspace flex flex-col min-h-full min-w-0" data-testid="canvas-analytics-view" data-canvas-analytics>
      <DashboardDateProvider>
        <DashboardAttributionProvider>
          <CanvasAnalyticsContent />
        </DashboardAttributionProvider>
      </DashboardDateProvider>
    </div>
  );
}
